import React, { createContext, useContext, useMemo, useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';

const ScrollContext = createContext({
  variant: null,
  setVariant: () => {}
});

export const useScrollSensor = () => {
  return useContext(ScrollContext);
};

const ScrollSensor = ({ children }) => {
  const [variant, setVariant] = useState(null);
  const router = useRouter();

  const activeSectionColor = useRef();

  useEffect(() => {
    const onScroll = () => {
      const sections = document.querySelectorAll('[data-variant]');
      const referencePoint = 40;

      const sectionPositions = Array.from(sections).map((section) => {
        const { variant, variantMaxWidth } = section.dataset;
        const { top, height, bottom } = section.getBoundingClientRect();

        return {
          top,
          height,
          bottom,
          variant,
          variantMaxWidth: variantMaxWidth ? Number(variantMaxWidth) : null,
          element: section
        };
      });

      // Find all sections that contain the reference point
      const containingSections = sectionPositions.filter(
        ({ top, bottom }) => top <= referencePoint && bottom > referencePoint
      );

      // If multiple sections contain the reference point (nested sections),
      // find the one that is closest to the reference point (smallest area = most nested)
      const viewportWidth = window.innerWidth;

      const activeSection = containingSections
        .filter((section) => {
          const { variantMaxWidth } = section;
          if (!variantMaxWidth) {
            return true;
          }

          return viewportWidth <= variantMaxWidth;
        })
        .reduce((closest, current) => {
          if (!closest) return current;

          // The section with smallest height is the most nested/closest one
          const currentArea = current.element.offsetWidth * current.height;
          const closestArea = closest.element.offsetWidth * closest.height;

          return currentArea < closestArea ? current : closest;
        }, null);

      const actualSectionColor = activeSection?.variant;

      if (actualSectionColor && actualSectionColor !== activeSectionColor.current) {
        setVariant(actualSectionColor);
        activeSectionColor.current = actualSectionColor;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Listen for route changes and trigger onScroll
    const handleRouteChange = () => {
      // Use setTimeout to ensure DOM is updated after route change
      setTimeout(onScroll, 0);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      window.removeEventListener('scroll', onScroll);
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const contextValue = useMemo(
    () => ({
      variant,
      setVariant
    }),
    [variant]
  );

  return <ScrollContext.Provider value={contextValue}>{children}</ScrollContext.Provider>;
};
export default ScrollSensor;
